import { useQuery } from '@tanstack/react-query'

import { SoftwareApi, type SoftwareSchema, type VersionSchema } from 'openapi'

import { SoftwareVersionBlock } from './SoftwareVersionBlock/SoftwareVersionBlock'
import { AdminButtonLink } from './components/AdminButtonLink'
import { YoutubeVideos } from './YoutubeVideos'
import { isUrl } from './utils'

type Props = {
  software: SoftwareSchema
  className?: string
}

export const SoftwarePageSidebar = ({ software, className }: Props) => {
  const { data: versions } = useQuery<VersionSchema[]>({
    queryKey: ['SoftwareApi.getLatestSofwareVersions', software.id],
    queryFn: () => SoftwareApi.getLatestSofwareVersions({ id: software.id }),
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  })

  if (!versions) return null
  const versionWithFilledComponents = versions.filter((version) => version.components.find((f) => f.trim()))

  return (
    <div className={`flex flex-col gap-y-5 ${className || ''}`}>
      <h2 className="inline-block">Developer: {software.vendor}</h2>
      {versionWithFilledComponents.map((version) => (
        <>
          <SoftwareVersionBlock versionId={version.id} softwareSlug={software.slug} key={version.id} />
        </>
      ))}
      <AdminButtonLink software={software} />

      {(software.youtubeReviewsUrls.length > 0 || software.youtubeTutorialsUrls.length > 0) && (
        <div className="flex flex-col w-full gap-4">
          {software.youtubeReviewsUrls.length > 0 && (
            <YoutubeVideos urls={software.youtubeReviewsUrls} title="Reviews" />
          )}
          {software.youtubeTutorialsUrls.length > 0 && (
            <YoutubeVideos urls={software.youtubeTutorialsUrls} title="Tutorials" />
          )}
        </div>
      )}
      <DocsUrls urls={software.documentationUrls} />
    </div>
  )
}

const DocsUrls = ({ urls }: { urls: string[] }) => {
  const filteredUrls = urls.filter(isUrl)
  if (!filteredUrls.length) return null
  return (
    <div className="bg-white flex flex-col gap-2 rounded">
      <div className="text-lg font-light -mt-0.5">Manuals and Docs</div>
      {filteredUrls.map((url, i) => {
        return (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            key={url + i}
            className="text-blue-700 hover:underline text-xs inline-block overflow-ellipsis overflow-hidden whitespace-nowrap"
          >
            {url}
          </a>
        )
      })}
    </div>
  )
}
